import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Contract, Lane, Regulation, Spot, Violation } from 'src/app/core/models';
import { ContractService, LaneService, RegulationService, SpotService, ViolationService } from 'src/app/core/services';
import { InfraliveFilters } from '../filters/filters.component';
import { MediaZipService } from 'src/app/core/services/media-zip.service';

@Component({
  selector: 'app-infra-live-v2',
  templateUrl: './infra-live-v2.component.html',
  styleUrls: ['./infra-live-v2.component.sass'],
})
export class InfraLiveV2Component implements OnInit, OnDestroy {

  public lastData: Violation;
  public connected = true;
  public violations: Violation[] = [];
  public violationData: Array<{ passId: string; spot: Spot; lane: Lane; regulation: Regulation }> = [];
  public loadingList = false;
  public viewFilters = false;
  public contractId: number;
  public contract: Contract;
  public filterParams: InfraliveFilters;
  public fixedLastData = false;
  public paused = false;
  public closedPanel = false;
  public currentFileView = 0;
  public currentViolationView = 0;
  public downloadFilesLoading = false;
  public loadingSpotLane = false;

  private pollingInterval: any;
  private readonly POLL_INTERVAL_MS = 5000;

  constructor(
    public activatedRoute: ActivatedRoute,
    private laneService: LaneService,
    private route: ActivatedRoute,
    private spotService: SpotService,
    private regulationService: RegulationService,
    private contractService: ContractService,
    private zipService: MediaZipService,
    private violationService: ViolationService
  ) { }

  ngOnInit(): void {
    this.contractId = this.activatedRoute.snapshot.params.contractId;

    this.getContract();
    this.loadInitialViolations();
    this.startPolling();

    document.addEventListener('selectedViolation', () => this.currentFileView = 0);
    document.addEventListener('openOrCloseFilters', (data: CustomEvent) => this.viewFilters = data.detail.viewFilters);
  }

  private startPolling(): void {
    this.pollingInterval = setInterval(() => this.pollViolations(), this.POLL_INTERVAL_MS);
  }

  async getContract() {
    this.contract = await this.contractService.getById(this.contractId);
  }

  async getLaneByCode(passId: string, code: string, number: any, regulationId: any) {
    this.loadingSpotLane = true;
    try {
      const contractId = this.route.snapshot.params.contractId;
      const response = await this.laneService.list({ code, contractId, number });
      let lane: Lane = null;
      let spot: Spot = null;
      if (response.result.length > 0) {
        lane = response.result[0];
        spot = await this.getSpotById(lane.spotId);
      }
      const regulation = await this.getRegulationById(regulationId);
      this.violationData.push({ passId, spot, lane, regulation });
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingSpotLane = false;
    }
  }

  async getSpotById(id: any) {
    try {
      return await this.spotService.getById(id);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getRegulationById(id: any) {
    try {
      return await this.regulationService.getById(id);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private buildQueryParams(): { params: any; filters: any } {
    const filters = JSON.parse(localStorage.getItem('infraliveFilters') || '{}');
    const limit = filters.registerAmount || 10;
    const params: any = {
      contractId: this.contractId,
      limit: String(limit),
      order: 'date',
      sortDesc: 'true',
    };
    if (filters.regulations?.length) {
      params['regulationId[in]'] = `[${filters.regulations}]`;
    }
    if (filters.spots?.length) {
      params['spotCode[in]'] = `[${filters.spots}]`;
    }
    if (filters.lanes?.length) {
      params['laneCode[in]'] = `[${filters.lanes}]`;
    }
    return { params, filters };
  }

  private applyClientFilters(results: Violation[], filters: any): Violation[] {
    if (!filters.enableNotPlate && filters.enableNotPlate !== undefined) {
      results = results.filter(v => !!v.vehicle?.plate?.trim());
    }
    if (filters.classification?.length) {
      const classIds = filters.classification.map(Number);
      results = results.filter(v => classIds.includes(Number(v.vehicle?.classificationCode)));
    }
    return results;
  }

  private sortFilesZoomFirst(files: any[]): void {
    files?.sort((a, b) => {
      if (a.type === 'zoom') { return -1; }
      if (b.type === 'zoom') { return 1; }
      return 0;
    });
  }

  async loadInitialViolations() {
    this.loadingList = true;
    try {
      const { params, filters } = this.buildQueryParams();
      const response = await this.violationService.list(params);
      const results = this.applyClientFilters(response.result, filters);

      results.forEach(v => this.sortFilesZoomFirst(v.files));

      this.violations = results;
      this.violations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      if (this.violations.length > 0) {
        this.lastData = this.violations[0];
      }

      for (const v of this.violations) {
        this.getLaneByCode(v.passId, v.laneCode, v.laneNumber, v.regulationId);
      }
    } catch (error) {
      console.error('Erro ao carregar infrações iniciais:', error);
    } finally {
      this.loadingList = false;
    }
  }

  async pollViolations() {
    if (this.paused) { return; }
    try {
      const { params, filters } = this.buildQueryParams();
      const response = await this.violationService.list(params);
      const fresh = this.applyClientFilters(response.result, filters);

      let hasNew = false;
      for (const incoming of fresh) {
        const idx = this.violations.findIndex(v => v.passId === incoming.passId);
        if (idx !== -1) {
          const currentCount = this.violations[idx].files?.length ?? 0;
          const incomingCount = incoming.files?.length ?? 0;
          if (incomingCount > currentCount) {
            this.sortFilesZoomFirst(incoming.files);
            this.violations[idx].files = incoming.files;
            if (this.lastData?.passId === incoming.passId && !this.fixedLastData) {
              this.lastData.files = incoming.files;
            }
          }
        } else {
          this.sortFilesZoomFirst(incoming.files);
          this.violations.push(incoming);
          hasNew = true;
          this.getLaneByCode(incoming.passId, incoming.laneCode, incoming.laneNumber, incoming.regulationId);
        }
      }

      if (hasNew) {
        this.violations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const filtersData = JSON.parse(localStorage.getItem('infraliveFilters') || '{}');
        const limit = filtersData.registerAmount || 10;
        this.violations = this.violations.slice(0, limit);

        if (!this.fixedLastData) {
          this.lastData = this.violations[0];
          this.currentViolationView = 0;
          this.currentFileView = 0;
        }
      }
    } catch (error) {
      console.error('Erro no polling de infrações:', error);
    }
  }

  setFilters(f: InfraliveFilters, onpenOrCLoseFilters = true) {
    if (onpenOrCLoseFilters) {
      this.openOrCloseFilters();
    }
    this.violations = [];
    this.violationData = [];
    this.lastData = undefined;
    this.currentViolationView = 0;
    this.currentFileView = 0;
    this.loadInitialViolations();
  }

  getRegulationByPassId(passId: string) {
    return this.violationData.find(a => a.passId === passId)?.regulation;
  }

  getSpotByPassId(passId: string) {
    return this.violationData.find(a => a.passId === passId)?.spot?.description || '-';
  }

  openOrCloseFilters() {
    this.viewFilters = !this.viewFilters;
    const event = new CustomEvent('openOrCloseFilters', { detail: { viewFilters: this.viewFilters } });
    document.dispatchEvent(event);
  }

  setFixedLastData() {
    if (this.lastData !== undefined) {
      this.fixedLastData = !this.fixedLastData;
    }
  }

  nextFileView() {
    if (this.lastData && this.lastData.files.length > 0) {
      this.currentFileView = (this.currentFileView + 1) % this.lastData.files.length;
    }
  }

  previousFileView() {
    if (this.lastData && this.lastData.files.length > 0) {
      this.currentFileView =
        (this.currentFileView - 1 + this.lastData.files.length) % this.lastData.files.length;
    }
  }

  updateViolation() {
    this.lastData = this.violations[this.currentViolationView] ?? null;
  }

  prevViolation() {
    if (this.violations.length === 0) { return; }
    this.currentViolationView = Math.max(0, this.currentViolationView - 1);
    this.updateViolation();
  }

  nextViolation() {
    if (this.violations.length === 0) { return; }
    this.currentViolationView = Math.min(this.violations.length - 1, this.currentViolationView + 1);
    this.updateViolation();
  }

  getRegulationColor(regulationId: number) {
    const target = this.contract?.ContractConfigColors?.find(a => a.regulationId === regulationId);
    return target ? target.color : '';
  }

  async downloadFiles() {
    if (this.lastData !== undefined) {
      this.downloadFilesLoading = true;
      this.fixedLastData = true;
      await this.zipService.downloadZip(this.lastData);
      this.downloadFilesLoading = false;
      this.fixedLastData = false;
    }
  }

  openPanel() {
    this.closedPanel = false;
    const event = new CustomEvent('openPanel', { detail: true });
    document.dispatchEvent(event);
  }

  ngOnDestroy(): void {
    localStorage.removeItem('infraliveFilters');
    clearInterval(this.pollingInterval);
  }
}
